import { useState, useRef, useEffect, useMemo } from "react";
import { Icon as IconifyIcon } from "@iconify/react";
import { ICON_CATALOG, getBrandColor } from "@/config/iconCatalog";

interface Props {
  currentIcon?: string;
  onSave: (icon: string) => void;
  onClose: () => void;
  className?: string;
}

export default function IconPicker({ currentIcon, onSave, onClose, className = "" }: Props) {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const q = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return [];
    return ICON_CATALOG.filter((e) => e.name.includes(q) || e.label.toLowerCase().includes(q));
  }, [q]);

  return (
    <div
      className={`bg-[#1c222b] border border-white/10 rounded-xl shadow-2xl p-3 ${className}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-1 mb-3 items-center">
        <span className="text-xs font-medium text-slate-400 px-1">Icons</span>
        {currentIcon && (
          <button
            className="ml-auto text-[11px] text-slate-400 hover:text-slate-100 transition-colors"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onSave(""); onClose(); }}
          >
            Clear
          </button>
        )}
      </div>

      <input
        ref={searchRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        placeholder="Search… aws, postgres, react"
        className="w-full bg-[#0f141a] border border-white/10 text-xs text-slate-100 outline-none placeholder:text-slate-500 focus:border-indigo-500/60 px-2.5 py-1.5 rounded-lg mb-2"
      />
      {!q && (
        <p className="text-xs text-slate-500 text-center py-2">Type to search icons</p>
      )}
      {q && filtered.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-2">No results</p>
      )}
      {filtered.length > 0 && (
        <div className="grid grid-cols-8 gap-0.5">
          {filtered.map((entry) => {
            const brandColor = getBrandColor(entry.iconifyId);
            return (
              <button
                key={entry.iconifyId}
                title={entry.label}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSave(entry.iconifyId); onClose(); }}
                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                  currentIcon === entry.iconifyId
                    ? "bg-canvas-accent/20 ring-1 ring-canvas-accent"
                    : "hover:bg-white/10"
                }`}
              >
                <IconifyIcon
                  icon={entry.iconifyId}
                  width={18}
                  height={18}
                  style={brandColor ? { color: brandColor } : undefined}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
